import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }
});

const BOOK_COVER = "https://customer-assets-eiarnc6j.emergentagent.net/job_inre-ro-metoden/artifacts/3yjoatlh_Bokomslag.pdf%20%282%29.png";
const BUY_URL = "https://leiladadgar.se/har-kan-du-bestalla-mina-bocker/";

export default function BookPage() {
  return (
    <div className="min-h-screen bg-white" data-testid="book-page">
      <Navigation />
      <div className="pt-28 sm:pt-36 pb-16 sm:pb-24 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">

          {/* Hero */}
          <motion.div {...fadeUp()} className="text-center mb-10 sm:mb-14">
            <p className="text-xs sm:text-sm uppercase tracking-[0.25em] font-medium text-[#A0655A] mb-4">
              Boken
            </p>
            <h1 className="heading-font text-3xl sm:text-4xl lg:text-5xl font-medium text-[#0A0A0A] leading-tight mb-4" data-testid="book-title">
              Din väg till emotionell frihet
            </h1>
            <p className="text-[#333] text-base sm:text-lg">
              Av Leila Dadgar
            </p>
          </motion.div>

          {/* Book cover */}
          <motion.div {...fadeUp(0.1)} className="flex justify-center mb-12 sm:mb-16">
            <div className="w-full max-w-xs sm:max-w-sm">
              <img
                src={BOOK_COVER}
                alt="Din väg till emotionell frihet — bokens omslag"
                className="w-full rounded-lg shadow-2xl shadow-black/15"
                data-testid="book-cover-image"
              />
            </div>
          </motion.div>

          {/* Buy button */}
          <motion.div {...fadeUp(0.15)} className="text-center mb-14">
            <Button
              onClick={() => window.open(BUY_URL, '_blank')}
              className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full px-10 py-6 text-base"
              data-testid="buy-book-btn"
            >
              Beställ boken <ExternalLink size={16} className="ml-2" />
            </Button>
          </motion.div>

          {/* About the book */}
          <motion.div {...fadeUp(0.2)} className="max-w-xl mx-auto mb-14">
            <div className="space-y-5 text-[#333] leading-relaxed text-base">
              <p>
                Den här boken är skriven för dig som känner att något skaver. Du fungerar, tar ansvar och gör det som förväntas — men under ytan finns en känsla som inte riktigt går att förklara. En oro, en trötthet eller en tomhet som följer med dig.
              </p>
              <p>
                I boken guidar jag dig genom en resa mot emotionell frihet — inte som en snabb lösning, utan som en process som sker i din egen takt. Du får förstå varför du reagerar som du gör, varifrån dina mönster kommer, och hur du steg för steg kan börja leva mer i linje med dig själv.
              </p>
              <p>
                Boken tar upp det vi sällan pratar om: den inre kritikern, känslor vi undviker, kroppens tysta signaler, gränser vi inte vågar sätta och den sårbarhet som krävs för att leva fritt.
              </p>
            </div>
          </motion.div>

          {/* Book + App connection */}
          <motion.div {...fadeUp(0.25)} className="max-w-xl mx-auto mb-14">
            <div className="bg-[#F5F0EB] rounded-2xl p-6 sm:p-8 border border-[#E8E0DB]/50">
              <h3 className="heading-font text-xl sm:text-2xl font-medium text-[#0A0A0A] mb-4 text-center">
                Boken och appen — tillsammans
              </h3>
              <div className="space-y-4 text-[#333] text-sm leading-relaxed">
                <p>
                  Boken ger dig förståelsen. Appen ger dig praktiken. Tillsammans bildar de en komplett resa.
                </p>
                <p>
                  I boken utforskar du varför du bär det du bär, var dina mönster kommer ifrån och vad emotionell frihet egentligen innebär. I appen omsätter du den förståelsen i handling — genom reflektioner, övningar, självtester och en personlig dagbok.
                </p>
                <p>
                  De är skapade för att komplettera varandra. Du kan börja med vilken du vill — men den som använder båda får mest ut av resan.
                </p>
              </div>
            </div>
          </motion.div>

          {/* 14 chapters overview */}
          <motion.div {...fadeUp(0.3)} className="max-w-xl mx-auto mb-14">
            <h3 className="heading-font text-xl sm:text-2xl font-medium text-[#0A0A0A] mb-6 text-center">
              Bokens 14 kapitel
            </h3>
            <div className="space-y-2">
              {[
                "När livet skaver och varför vi så sällan stannar upp",
                "Att börja se sig själv på riktigt",
                "Varför du reagerar som du gör",
                "Mönster som håller dig kvar",
                "Din inre röst",
                "Känslor du försöker undvika",
                "När kroppen säger ifrån",
                "Att släppa taget om det som varit",
                "Vem är du utan dina mönster?",
                "Att börja välja annorlunda",
                "Att sätta gränser och stå kvar i sig själv",
                "Att skapa en trygg relation till sig själv",
                "När livet fortsätter hända",
                "Att leva mer fritt",
              ].map((chapter, i) => (
                <div key={i} className="flex gap-3 py-2.5 px-3 rounded-lg" data-testid={`chapter-${i + 1}`}>
                  <span className="text-[#A0655A] text-sm font-medium w-6 flex-shrink-0 text-right">{i + 1}.</span>
                  <span className="text-[#1A1A1A] text-sm">{chapter}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Bottom CTA */}
          <motion.div {...fadeUp(0.35)} className="text-center">
            <Button
              onClick={() => window.open(BUY_URL, '_blank')}
              className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full px-10 py-6 text-base"
              data-testid="buy-book-btn-bottom"
            >
              Beställ boken <ExternalLink size={16} className="ml-2" />
            </Button>
          </motion.div>

        </div>
      </div>
      <Footer />
    </div>
  );
}
